import validator from 'validator';
// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import props.
import {
	InputFormProps,
	InputTextWithoutInputTextBoxProps,
} from '@app/props';

interface UseInputCreatePasswordParameter {
	id: string;
	required: boolean;
	disabled: boolean;
	showValidation?: boolean;
}

type UseInputNewPasswordResults = UseInputFormResult<string, InputTextWithoutInputTextBoxProps>;
type UseInputConfirmPasswordResults = UseInputFormResult<string, InputTextWithoutInputTextBoxProps>;
interface UseInputCreatePasswordResults {
    passwordInput: UseInputNewPasswordResults;
    confirmPasswordInput: UseInputConfirmPasswordResults;
}

const useInputCreatePassword = (params: UseInputCreatePasswordParameter): UseInputCreatePasswordResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyNewPasswordInputProps = (): InputTextWithoutInputTextBoxProps => {
		// First apply the form input props.
		const formProps: InputFormProps = newPasswordInput.applyProps();
		// Then apply the text input props.
		return {
			...formProps,
			infoText: 'Must be at least 8 characters long and contain a capital letter, a lowercase letter, a number, and a symbol such as: (@#$%^&*)',
			isPassword: true,
			onChange: newPasswordInput.setInput,
			value: newPasswordInput.value,
		}
	};

    const applyConfirmPasswordInputProps = (): InputTextWithoutInputTextBoxProps => {
		// First apply the form input props.
		const formProps: InputFormProps = confirmPasswordInput.applyProps();
		// Then apply the text input props.
		return {
			...formProps,
			isPassword: true,
			onChange: confirmPasswordInput.setInput,
			value: confirmPasswordInput.value,
		}
	};

	// #endregion exported methods


	// #region private methods

	/**
	 * Validates the current value.
	 * @param value
	 */
	const validateNewPassword = (value: string, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& String.isNullOrWhiteSpace(value)) {
			response = new ValidatorResponse(false, 'New password is required.');
		} 
        else if (!validator.isStrongPassword(value)) {
			response = new ValidatorResponse(false, 'Please make sure the new password is 8 characters long and has a capital letter, a lowercase letter, a number, and a symbol.')
		}

		return response;
	}

    const validateConfirmPassword = (value: string, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& String.isNullOrWhiteSpace(value)) {
			response = new ValidatorResponse(false, 'Please confirm your new password.');
		} 
        else if (value !== newPasswordInput.value){
			response = new ValidatorResponse(false, 'New password does not match.');
		}
		return response;
	}
	// #endregion private methods


	// #region variables

	// Instantiate the 'text' input.
	const newPasswordInput = useInputForm<string>({
		id: params.id,
		label: 'New Password',
		required: params.required,
		disabled: params.disabled,
		initialInput: String.empty(),
		showValidation: params.showValidation,
		validators: [validateNewPassword],
	});

    const confirmPasswordInput = useInputForm<string>({
		id: 'confirm-password',
		label: 'Confirm Password',
		required: params.required,
		disabled: params.disabled,
		initialInput: String.empty(),
		showValidation: params.showValidation,
		validators: [validateConfirmPassword],
	});
	// #endregion variables


	return {
		passwordInput: {
            ...newPasswordInput,
            applyProps: applyNewPasswordInputProps,
        },
        confirmPasswordInput: {
            ...confirmPasswordInput,
            applyProps: applyConfirmPasswordInputProps,
        }
	};
}

// Export parameters.
export {
	UseInputCreatePasswordParameter,
}

// Export results.
export {
	UseInputCreatePasswordResults,
}

export default useInputCreatePassword;